// Typed helpers for /api/compose (docs/design/compose.md): the owner's own drafts. Nothing here
// sends: `send` asks the server for an approval bound to the exact draft, which the executor
// runs after the undo window; `undo` cancels it inside that window. URL builders are pure so
// they can be unit-tested.
import type {
  ComposeDraftPatch,
  ComposeDraftRequest,
  ComposeDraftState,
  ComposeDraftView,
  ComposeIdentitiesView,
  ComposePreview,
  ComposeRemindDays,
  ComposeSendResult,
  ComposeStartRequest,
  ComposeStartResult,
} from "@shared/api";
import { api } from "./api";

const enc = encodeURIComponent;

export interface ComposeListParams {
  states?: readonly ComposeDraftState[] | undefined;
  accountId?: string | undefined;
  limit?: number | undefined;
}

export const composeUrls = {
  identities: (accountId: string) => `/api/compose/identities/${enc(accountId)}`,
  list: (params: ComposeListParams = {}) => {
    const search = new URLSearchParams();
    if (params.states?.length) search.set("state", params.states.join(","));
    if (params.accountId) search.set("accountId", params.accountId);
    if (params.limit) search.set("limit", String(params.limit));
    const text = search.toString();
    return `/api/compose${text ? `?${text}` : ""}`;
  },
  create: () => "/api/compose",
  draft: (id: string) => `/api/compose/${enc(id)}`,
  preview: (id: string) => `/api/compose/${enc(id)}/preview`,
  send: (id: string) => `/api/compose/${enc(id)}/send`,
  undo: (id: string) => `/api/compose/${enc(id)}/undo`,
  restore: (id: string) => `/api/compose/${enc(id)}/restore`,
  start: () => "/api/compose/start",
  attachments: (id: string) => `/api/compose/${enc(id)}/attachments`,
  attachment: (id: string, attId: string) => `/api/compose/${enc(id)}/attachments/${enc(attId)}`,
  restoreAttachment: (id: string, attId: string) =>
    `/api/compose/${enc(id)}/attachments/${enc(attId)}/restore`,
};

export const composeApi = {
  identities: (accountId: string, signal?: AbortSignal) =>
    api<ComposeIdentitiesView>(composeUrls.identities(accountId), { signal }),
  list: (params: ComposeListParams, signal?: AbortSignal) =>
    api<ComposeDraftView[]>(composeUrls.list(params), { signal }),
  create: (body: ComposeDraftRequest) =>
    api<ComposeDraftView>(composeUrls.create(), { method: "POST", body }),
  get: (id: string, signal?: AbortSignal) =>
    api<ComposeDraftView>(composeUrls.draft(id), { signal }),
  patch: (id: string, body: ComposeDraftPatch) =>
    api<ComposeDraftView>(composeUrls.draft(id), { method: "PATCH", body }),
  discard: (id: string) => api<ComposeDraftView>(composeUrls.draft(id), { method: "DELETE" }),
  /** What the gateway would bind if this draft were sent now (computed on the server). */
  preview: (id: string, signal?: AbortSignal) =>
    api<ComposePreview>(composeUrls.preview(id), { signal }),
  /**
   * Asks for the approval of what the owner reviewed: the revision and the preview's
   * fingerprint (409 `stale` with reason `changed_since_review` if it changed since);
   * `remindDays` is the "remind me if they don't reply" choice; `sendAt` schedules it (U12).
   */
  send: (
    id: string,
    rev: number,
    fingerprint: string,
    remindDays?: ComposeRemindDays,
    sendAt?: string | null,
  ) =>
    api<ComposeSendResult>(composeUrls.send(id), {
      method: "POST",
      body: {
        rev,
        fingerprint,
        ...(remindDays === undefined ? {} : { remindDays }),
        ...(sendAt ? { sendAt } : {}),
      },
    }),
  undo: (id: string) => api<ComposeDraftView>(composeUrls.undo(id), { method: "POST" }),
  /** Undo of a discard: the draft and its files come back. */
  restore: (id: string) => api<ComposeDraftView>(composeUrls.restore(id), { method: "POST" }),
  /** Reply, Reply all or Forward a cached thread: a new draft addressed by the server. */
  start: (body: ComposeStartRequest) =>
    api<ComposeStartResult>(composeUrls.start(), { method: "POST", body }),
  upload: (id: string, file: File) =>
    api<ComposeDraftView>(composeUrls.attachments(id), {
      method: "POST",
      upload: { file, headers: { "x-file-name": encodeURIComponent(file.name || "attachment") } },
    }),
  removeAttachment: (id: string, attId: string) =>
    api<ComposeDraftView>(composeUrls.attachment(id, attId), { method: "DELETE" }),
  restoreAttachment: (id: string, attId: string) =>
    api<ComposeDraftView>(composeUrls.restoreAttachment(id, attId), { method: "POST" }),
};
